import { useState, useEffect } from 'react';
import { Typography } from '@mui/material';
import { useAppContext } from '../../../utils/content.util';
import { getDocuments } from '../../../services/documents.service';
import { Document } from '../../../models/document.model';
import { getNewsList } from '../../../services/news.service';
import { News } from '../../../models/news.model';
import { onError } from '../../../utils/error.util';
import { CompanyConfig } from '../../../company.config';

/** YYYY-MM-DD as a local calendar day (Date.parse would treat it as UTC). */
function formatNewsDashboardDate(displayDate: string): string {
  const [y, m, d] = displayDate.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function DashboardContainer() {
  const { currentUser } = useAppContext();
  const [activeTab, setActiveTab] = useState(0);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [titles, setTitles] = useState<string[]>([]);
  const [news, setNews] = useState<News[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingNews, setIsLoadingNews] = useState(true);

  useEffect(() => {
    loadDocuments();
    loadNews();
  }, []);

  async function loadDocuments() {
    try {
      const result = await getDocuments({ limit: 100 });
      setDocuments(result.items);

      // Extract unique titles
      const uniqueTitles = Array.from(new Set(result.items.map((doc) => doc.title)));
      setTitles(uniqueTitles);
    } catch (e: any) {
      onError(e);
    } finally {
      setIsLoading(false);
    }
  }

  async function loadNews() {
    try {
      const shortName = CompanyConfig.shortName.toLowerCase();

      const result = await getNewsList({ limit: 100, division: shortName });
      setNews(result.items);
    } catch (e: any) {
      onError(e);
    } finally {
      setIsLoadingNews(false);
    }
  }

  function getDocumentsByTitle(title: string): Document[] {
    return documents.filter((doc) => doc.title === title);
  }

  // Format current date
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const handleFaqClick = (index: number) => {
    setActiveTab(index);
  };

  return (
    <main id="main">
      <div id="dash-bg">
        <section className="py-3" id="dash-header">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <h1>Hello, {currentUser?.given_name || 'NAME'}</h1>
                <p className="m-0">{currentDate}</p>
                <div className="liner"></div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-4" id="dash-whats-new">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <h2>What's New!</h2>
              </div>
            </div>
            <div className="row">
              {isLoadingNews ? (
                <div className="col-12">
                  <Typography>Loading news...</Typography>
                </div>
              ) : news.length === 0 ? (
                <div className="col-12">
                  <Typography>No news available</Typography>
                </div>
              ) : (
                news.map((newsItem) => (
                  <div key={newsItem.id} className="col-12 col-md-6">
                    <article>
                      <h3>{newsItem.title}</h3>
                      <div dangerouslySetInnerHTML={{ __html: newsItem.htmlContent }} />
                      <p className="news-date">
                        {formatNewsDashboardDate(newsItem.displayDate)}
                      </p>
                    </article>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </div>

      <section className="py-5" id="qa">
        <div className="container py-5">
          <div className="row">
            <div className="col-12">
              <h2>Helpful Information</h2>
            </div>
            <div className="col-12 col-lg-5 col-xl-5 mx-auto mb-4 mb-lg-0">
              {isLoading ? (
                <Typography>Loading documents...</Typography>
              ) : titles.length === 0 ? (
                <Typography>No documents available</Typography>
              ) : (
                <ul className="faq-links">
                  {titles.map((title, index) => (
                    <li
                      key={index}
                      id={`faq${index + 1}`}
                      className={activeTab === index ? 'activate' : ''}
                      onClick={() => handleFaqClick(index)}
                      title={title}
                    >
                      {title}
                      {activeTab !== index && <span className="fa fa-angle-right"></span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="col-11 col-sm-10 col-md-9 col-lg-7 col-xl-7 mx-auto pt-2 pt-md-0">
              <div className="faq-results">
                {!isLoading &&
                  titles.map((title, index) => (
                    <section
                      key={index}
                      className={`faq${index + 1}`}
                      style={{ display: activeTab === index ? 'block' : 'none' }}
                    >
                      <article>
                        <h3>{title}</h3>
                        {getDocumentsByTitle(title).map((doc) => (
                          <div key={doc.id} dangerouslySetInnerHTML={{ __html: doc.htmlContent }} />
                        ))}
                      </article>
                    </section>
                  ))}
              </div>
            </div>
          </div>

        </div>
      </section>
    </main>
  );
}
